import { parsePhoneNumberFromString, validatePhoneNumberLength } from 'libphonenumber-js/min';

type TranslateFn = (scope: string, options?: any) => string;

type CanadianPhoneValidationReason = 'required' | 'tooShort' | 'tooLong' | 'notCanadian' | 'invalid';

interface CanadianPhoneValidationResult {
  valid: boolean;
  reason?: CanadianPhoneValidationReason;
  e164?: string;
}

const CANADIAN_COUNTRY_CODE = 'CA';

const normalizePhoneInput = (value?: string) => value?.trim() ?? '';

const getPhoneParseInput = (value?: string) => {
  const trimmedValue = normalizePhoneInput(value);
  const digitsOnly = trimmedValue.replace(/\D/g, '');

  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  }

  return trimmedValue;
};

export const validateCanadianPhoneNumber = (value?: string): CanadianPhoneValidationResult => {
  const trimmedValue = normalizePhoneInput(value);

  if (!trimmedValue) {
    return { valid: false, reason: 'required' };
  }

  const parseInput = getPhoneParseInput(trimmedValue);
  const lengthResult = validatePhoneNumberLength(parseInput, CANADIAN_COUNTRY_CODE);

  if (lengthResult) {
    if (lengthResult === 'TOO_SHORT') {
      return { valid: false, reason: 'tooShort' };
    }

    if (lengthResult === 'TOO_LONG') {
      return { valid: false, reason: 'tooLong' };
    }

    return { valid: false, reason: 'invalid' };
  }

  const phoneNumber = parsePhoneNumberFromString(parseInput, CANADIAN_COUNTRY_CODE);

  if (!phoneNumber || !phoneNumber.isValid()) {
    return { valid: false, reason: 'invalid' };
  }

  if (phoneNumber.country !== CANADIAN_COUNTRY_CODE) {
    return { valid: false, reason: 'notCanadian' };
  }

  return {
    valid: true,
    e164: phoneNumber.number,
  };
};

export const isCanadianPhoneNumberValid = (value?: string) => validateCanadianPhoneNumber(value).valid;

export const toCanadianPhoneE164 = (value?: string) => validateCanadianPhoneNumber(value).e164;

export const getCanadianPhoneValidationState = (
  value: string | undefined,
  translate: TranslateFn,
  options?: { required?: boolean },
): { valid: boolean; message: string } => {
  const required = options?.required ?? false;
  const trimmedValue = normalizePhoneInput(value);

  if (!trimmedValue) {
    return {
      valid: !required,
      message: required ? translate('field_required') : '',
    };
  }

  const validation = validateCanadianPhoneNumber(trimmedValue);

  if (validation.valid) {
    return { valid: true, message: '' };
  }

  return {
    valid: false,
    message: translate('field_value_not_valid'),
  };
};
